import { useState } from 'react';
import { compileCue, describeCue } from '../../av/cues';
import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../av/time';
import { TEMPLATES, TEMPLATE_IDS, type TemplateId } from '../../animation/procedural/spec';
import * as av from '../../domain/avOps';
import type { ConflictPolicy } from '../../domain/motionOps';
import type { AvSession, Project } from '../../domain/schemas';
import type { SyncSelectionState, SyncSelectionStore } from '../../state/syncSelection';
import type { EditFn } from '../scene/useSceneEdit';
import { Button, inputClass } from '../ui';

/**
 * Motion cues: Phase 3C motions on the musical timeline ("jump every four bars", "start
 * running at bar 3"). They play as reactive overlays; "Bake into keyframes" compiles them
 * through the Phase 3C compiler into permanent keyframes of the animation (one undo step).
 */
export function MotionCuesPanel({ project, session, sel, selection, edit }: { project: Project; session: AvSession; sel: SyncSelectionState; selection: SyncSelectionStore; edit: EditFn }) {
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const hero = scene.objects.find((o) => o.type === 'character');
  const [template, setTemplate] = useState<TemplateId>('jump');
  const [objectId, setObjectId] = useState(hero?.id ?? scene.objects[0]?.id ?? '');
  const [bar, setBar] = useState(1);
  const [beat, setBeat] = useState(1);
  const [everyBars, setEveryBars] = useState(0);
  const [count, setCount] = useState(1);
  const [policy, setPolicy] = useState<ConflictPolicy>('merge');
  const [message, setMessage] = useState<string | null>(null);
  const object = scene.objects.find((o) => o.id === objectId);
  const templates = TEMPLATE_IDS.filter((t) => t !== 'path' && (!TEMPLATES[t].characterOnly || object?.type === 'character'));
  const add = () => {
    let id = '';
    const ok = edit(`Add ${TEMPLATES[template].label.toLowerCase()} cue`, (p) => {
      const r = av.addMotionCue(p, session.id, {
        template,
        objectId,
        tick: (bar - 1) * TICKS_PER_BAR + (beat - 1) * TICKS_PER_BEAT,
        everyTicks: everyBars > 0 ? everyBars * TICKS_PER_BAR : null,
        count: everyBars > 0 ? count : 1,
        durationBeats: null,
        params: {},
      });
      id = r.id;
      return r.project;
    });
    if (ok) selection.update({ cueId: id });
  };
  const bake = () => {
    let summary = '';
    if (
      edit('Bake motion cues', (p) => {
        const r = av.applyMotionCues(p, session.id, policy, true);
        summary = r.summary;
        return r.project;
      })
    ) {
      setMessage(`Baked into permanent keyframes: ${summary}`);
      selection.update({ cueId: null });
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Object
          <select className={inputClass} value={objectId} onChange={(e) => setObjectId(e.target.value)} data-testid="cue-object">
            {scene.objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Motion
          <select className={inputClass} value={templates.includes(template) ? template : templates[0]} onChange={(e) => setTemplate(e.target.value as TemplateId)} data-testid="cue-template">
            {templates.map((t) => (
              <option key={t} value={t}>
                {TEMPLATES[t].label}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Bar
            <input className={inputClass} type="number" min={1} max={256} value={bar} onChange={(e) => setBar(Math.max(1, Math.round(Number(e.target.value) || 1)))} data-testid="cue-bar" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Beat
            <input className={inputClass} type="number" min={1} max={4} value={beat} onChange={(e) => setBeat(Math.min(4, Math.max(1, Math.round(Number(e.target.value) || 1))))} />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Repeat every (bars, 0 = once)
          <input className={inputClass} type="number" min={0} max={64} value={everyBars} onChange={(e) => setEveryBars(Math.max(0, Math.round(Number(e.target.value) || 0)))} data-testid="cue-every" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Times
          <input className={inputClass} type="number" min={1} max={64} value={count} disabled={everyBars === 0} onChange={(e) => setCount(Math.min(64, Math.max(1, Math.round(Number(e.target.value) || 1))))} />
        </label>
        <Button variant="primary" className="self-end" onClick={add} disabled={!object || templates.length === 0} data-testid="cue-add">
          Add motion cue
        </Button>
      </div>
      {session.motionCues.length > 0 && (
        <>
          <ul className="flex flex-col gap-1" aria-label="Motion cues" data-testid="cue-list">
            {session.motionCues.map((c) => {
              const compiled = compileCue(c, scene, project.characters, project.settings.bpm);
              return (
                <li key={c.id} className={`flex flex-wrap items-center gap-2 rounded-md px-2 py-1 text-sm ${c.id === sel.cueId ? 'bg-amber-950/50 ring-1 ring-amber-700' : 'bg-zinc-800/50'}`} data-testid="cue-row">
                  <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => selection.update({ cueId: c.id === sel.cueId ? null : c.id })} aria-pressed={c.id === sel.cueId}>
                    {describeCue(c)} <span className="text-xs text-zinc-400">· {scene.objects.find((o) => o.id === c.objectId)?.name}</span>
                    {compiled.error && <span className="text-xs text-red-300"> · {compiled.error}</span>}
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => edit('Remove motion cue', (p) => av.removeMotionCue(p, session.id, c.id))}>
                    Remove<span className="sr-only"> {describeCue(c)}</span>
                  </Button>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select className={`${inputClass} w-auto!`} value={policy} onChange={(e) => setPolicy(e.target.value as ConflictPolicy)} aria-label="When cues overlap existing keyframes">
              <option value="merge">merge with existing keys</option>
              <option value="replace-range">replace keys in the cue ranges</option>
              <option value="new-animation">into a new animation</option>
            </select>
            <Button size="sm" onClick={bake} data-testid="cue-bake">
              Bake into keyframes
            </Button>
            <span className="text-zinc-400">Cues play as overlays; baking makes them permanent keyframes (Undo restores the cues).</span>
          </div>
        </>
      )}
      {message && (
        <p className="text-xs text-emerald-300" role="status">
          {message}
        </p>
      )}
    </div>
  );
}
